import { type Spec } from "@json-render/react";
import { jsonrepair } from "jsonrepair";
import { isEdgenChartComponentType } from "../Charts/edgen-charts/chartComponentTypes";
import { parseTagStringAttribute } from "@/src/utils/parseDynamicUiBody";

interface DynamicUiItemRenderContext {
  collapsed?: boolean;
  notificationId?: string;
  metrics?: string;
  fallbackText?: string;
}

interface DynamicUiSpecItem {
  kind: "spec";
  spec: Spec;
  renderContext: DynamicUiItemRenderContext;
}

interface DynamicUiChartItem {
  kind: "chart";
  spec: Spec;
  renderContext: DynamicUiItemRenderContext;
}

export type DynamicUiItem = DynamicUiSpecItem | DynamicUiChartItem;

interface DynamicUiParseResult {
  markdown: string;
  items: DynamicUiItem[];
  hasPendingDynamicUi: boolean;
}

const OPEN_TAG = "<dynamic_ui";
const CLOSE_TAG = "</dynamic_ui>";

/**
 * Parse JSON with automatic repair for common LLM output issues
 * (missing closing brackets, trailing commas, etc.)
 */
const tryParseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    const repaired = jsonrepair(raw);
    return JSON.parse(repaired);
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isValidSpec = (value: unknown): value is Spec => {
  if (!isRecord(value)) {
    return false;
  }

  const { root, elements } = value;

  return (
    typeof root === "string" &&
    root.length > 0 &&
    isRecord(elements) &&
    isRecord(elements[root])
  );
};

const getRootElement = (spec: Spec): Record<string, unknown> | undefined => {
  const elements = spec.elements as Record<string, unknown>;
  const rootElement = elements[spec.root];

  return isRecord(rootElement) ? rootElement : undefined;
};

const isChartSpec = (spec: Spec): boolean =>
  isEdgenChartComponentType(getRootElement(spec)?.type);

const parseBooleanAttribute = (
  value: string | undefined,
): boolean | undefined => {
  if (value === "true") {
    return true;
  }

  if (value === "false") {
    return false;
  }

  return undefined;
};

/*
 * 开标签属性解析**共用** `utils/parseDynamicUiBody` 的实现 —— 这里原本另写了一份。
 *
 * 两份必然漂移，而且已经漂过：那份少了单引号支持、这份少了属性名边界
 * （`\b` 挡不住 `data-id=`，会取走别人的值）。而 `id` 正是通知卡
 * 「曝光 ↔ 反馈」的配对主键，取错是静默的 —— 2026-08-13 合成一份。
 */
const parseStringAttribute = parseTagStringAttribute;

const parseDynamicUiOpeningTag = (
  openingTag: string,
): DynamicUiItemRenderContext => {
  const collapsed = parseBooleanAttribute(
    parseStringAttribute(openingTag, "collapsed"),
  );
  const notificationId = parseStringAttribute(openingTag, "id");
  const metrics = parseStringAttribute(openingTag, "metrics");
  const fallbackText = parseStringAttribute(openingTag, "fallback");

  if (
    collapsed === undefined &&
    !notificationId &&
    metrics === undefined &&
    !fallbackText
  ) {
    return {};
  }

  return {
    ...(collapsed === undefined ? {} : { collapsed }),
    ...(notificationId ? { notificationId } : {}),
    ...(metrics === undefined ? {} : { metrics }),
    ...(fallbackText ? { fallbackText } : {}),
  };
};

const isValidOpenTagBoundary = (char: string | undefined): boolean =>
  char === undefined || char === ">" || /\s/.test(char);

const findNextOpenTag = (
  content: string,
  cursor: number,
): {
  openIndex: number;
  openTagEndIndex: number;
  openingTag: string | null;
} | null => {
  let searchIndex = cursor;

  while (searchIndex < content.length) {
    const openIndex = content.indexOf(OPEN_TAG, searchIndex);

    if (openIndex === -1) {
      return null;
    }

    const boundaryChar = content[openIndex + OPEN_TAG.length];

    if (!isValidOpenTagBoundary(boundaryChar)) {
      searchIndex = openIndex + OPEN_TAG.length;
      continue;
    }

    const openTagEndIndex = content.indexOf(">", openIndex + OPEN_TAG.length);

    if (openTagEndIndex === -1) {
      return {
        openIndex,
        openTagEndIndex,
        openingTag: null,
      };
    }

    return {
      openIndex,
      openTagEndIndex,
      openingTag: content.slice(openIndex, openTagEndIndex + 1),
    };
  }

  return null;
};

export const parseDynamicUiBlocks = (content: string): DynamicUiParseResult => {
  const items: DynamicUiItem[] = [];
  let markdown = "";
  let cursor = 0;
  let hasPendingDynamicUi = false;

  while (cursor < content.length) {
    const openTag = findNextOpenTag(content, cursor);

    if (!openTag) {
      markdown += content.slice(cursor);
      break;
    }

    markdown += content.slice(cursor, openTag.openIndex);

    if (openTag.openTagEndIndex === -1 || !openTag.openingTag) {
      hasPendingDynamicUi = true;
      markdown += `\n<chart-loading></chart-loading>\n`;
      break;
    }

    const jsonStart = openTag.openTagEndIndex + 1;
    const closeIndex = content.indexOf(CLOSE_TAG, jsonStart);

    if (closeIndex === -1) {
      hasPendingDynamicUi = true;
      markdown += `\n<chart-loading></chart-loading>\n`;
      break;
    }

    const rawJson = content.slice(jsonStart, closeIndex).trim();
    const renderContext = parseDynamicUiOpeningTag(openTag.openingTag);

    try {
      const parsed = tryParseJson(rawJson);

      if (parsed && isValidSpec(parsed)) {
        const index = items.length;
        const kind = isChartSpec(parsed) ? "chart" : "spec";
        items.push({
          kind,
          spec: parsed,
          renderContext,
        });
        markdown +=
          kind === "chart"
            ? `\n<edgen-chart-render data-index="${index}"></edgen-chart-render>\n`
            : `\n<chat-json-render data-index="${index}"></chat-json-render>\n`;
      } else if (renderContext.fallbackText) {
        markdown += renderContext.fallbackText;
      }
    } catch {
      if (renderContext.fallbackText) {
        markdown += renderContext.fallbackText;
      }
    }

    cursor = closeIndex + CLOSE_TAG.length;
  }

  return {
    markdown,
    items,
    hasPendingDynamicUi,
  };
};
